/**
 * Patient-portal PDF API — reuses EMR `pdfExport.js` so portal downloads match clinic printouts.
 */
import {
    generateGlassesPDF,
    generateExaminationPDF,
    generateMedicationPDF,
    generateInvestigationPDF,
    generateTreatmentPDF,
    generateLabPDF,
    generateSurgeryPDF,
    generateReportPDF,
} from '../utils/pdfExport';
import { buildReportPrintHtml, reportNeedsHtmlExport } from '../utils/reportPdfExport';

const safeFileSegment = (s) =>
    String(s || 'record').replace(/[^a-z0-9_.-]+/gi, '_').replace(/^_+|_+$/g, '') || 'record';

const recordDateForFile = (item) => {
    const d = item?.date || item?.datePerformed || item?.dateScheduled || item?.createdAt;
    if (!d) return '';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return safeFileSegment(d);
    return dt.toISOString().slice(0, 10);
};

const pdfBlobFromDoc = (doc) => doc.output('blob');

const portalPdfOptions = (patient, extra = {}) => ({
    returnDoc: true,
    patientId: patient?.id ?? null,
    referenceId: patient?.id ?? null,
    ...extra,
});

/** @returns {Promise<{ filename: string, blob: Blob }>} */
const buildGlasses = async (item, patient) => {
    const doc = await generateGlassesPDF(item, patient, portalPdfOptions(patient));
    return {
        filename: `glasses_${item.glassesType === 'old' ? 'old' : 'rx'}_${recordDateForFile(item) || safeFileSegment(item.id)}.pdf`,
        blob: pdfBlobFromDoc(doc),
    };
};

const buildMedication = async (item, patient) => {
    const doc = await generateMedicationPDF(item, patient, portalPdfOptions(patient, {
        isArabic: false,
    }));
    return {
        filename: `medication_${safeFileSegment(item.name)}_${recordDateForFile(item) || safeFileSegment(item.id)}.pdf`,
        blob: pdfBlobFromDoc(doc),
    };
};

const buildTreatment = async (item, patient) => {
    const doc = await generateTreatmentPDF(item, patient, portalPdfOptions(patient));
    return {
        filename: `treatment_${recordDateForFile(item) || safeFileSegment(item.id)}.pdf`,
        blob: pdfBlobFromDoc(doc),
    };
};

const buildInvestigation = async (item, patient) => {
    const doc = await generateInvestigationPDF(item, patient, portalPdfOptions(patient));
    return {
        filename: `investigation_${safeFileSegment(item.type)}_${recordDateForFile(item) || safeFileSegment(item.id)}.pdf`,
        blob: pdfBlobFromDoc(doc),
    };
};

const buildExamination = async (item, patient) => {
    const doc = await generateExaminationPDF(item, patient, portalPdfOptions(patient));
    return {
        filename: `examination_${recordDateForFile(item) || safeFileSegment(item.id)}.pdf`,
        blob: pdfBlobFromDoc(doc),
    };
};

const buildReport = async (item, patient) => {
    const isArabic = item?.language === 'ar';
    if (reportNeedsHtmlExport(item, isArabic)) {
        const html = buildReportPrintHtml(item, patient, { isArabic });
        return {
            filename: `report_${safeFileSegment(item.type) || 'report'}_${recordDateForFile(item) || safeFileSegment(item.id)}.html`,
            blob: new Blob([html], { type: 'text/html;charset=utf-8' }),
        };
    }
    const doc = await generateReportPDF(item, patient, portalPdfOptions(patient, { isArabic }));
    return {
        filename: `report_${safeFileSegment(item.type) || 'report'}_${recordDateForFile(item) || safeFileSegment(item.id)}.pdf`,
        blob: pdfBlobFromDoc(doc),
    };
};

const buildSurgery = async (item, patient) => {
    const doc = await generateSurgeryPDF(item, patient, portalPdfOptions(patient));
    return {
        filename: `surgery_${safeFileSegment(item.procedureName)}_${recordDateForFile(item) || safeFileSegment(item.id)}.pdf`,
        blob: pdfBlobFromDoc(doc),
    };
};

const buildLab = async (item, patient) => {
    const doc = await generateLabPDF(item, patient, portalPdfOptions(patient));
    return {
        filename: `lab_${safeFileSegment(item.panelType)}_${recordDateForFile(item) || safeFileSegment(item.id)}.pdf`,
        blob: pdfBlobFromDoc(doc),
    };
};

/** Section keys align with patient portal bundle + `PDF_BUILDERS` in portal.js */
export const PORTAL_PDF_BUILDERS = {
    glasses: { folder: 'Glasses', build: buildGlasses },
    medications: { folder: 'Medications', build: buildMedication },
    treatments: { folder: 'Treatments', build: buildTreatment },
    investigations: { folder: 'Investigations', build: buildInvestigation },
    examinations: { folder: 'Examinations', build: buildExamination },
    reports: { folder: 'Reports', build: buildReport },
    surgeries: { folder: 'Surgeries', build: buildSurgery },
    labs: { folder: 'Labs', build: buildLab },
};

/**
 * Build one portal record artifact (PDF, or printable HTML for Arabic-rich reports).
 * @param {string} sectionKey
 * @param {object} item
 * @param {object} patient
 */
export const buildPortalRecordPdf = async (sectionKey, item, patient) => {
    const entry = PORTAL_PDF_BUILDERS[sectionKey];
    if (!entry) throw new Error(`Unknown portal PDF section: ${sectionKey}`);
    return entry.build(item, patient);
};

export default {
    PORTAL_PDF_BUILDERS,
    buildPortalRecordPdf,
};
